import { affectsWorkspace } from '@/services/workspaceRefresh'
import { useActionTypes } from '@/helpers/useActionTypes'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Dropdown, Form, InputGroup, Modal, Row, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBinoculars, faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { ActionPresenceIcon } from '@/components/actions/ActionPresenceIcon'
import { LiveActionWorkspace } from '@/components/actions/LiveActionWorkspace'
import { AdminPageHero } from '@/components/common/AdminPageHero'
import { dueDateIso, DueDateField, todayDateKey } from '@/components/common/DueDateField'
import { Field } from '@/components/common/Field'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ResultsPager } from '@/components/common/ResultsPager'
import { useAuth } from '@/context/AuthContext'
import { forThisBusiness } from '@/helpers/businessName'
import { useCrmHub } from '@/context/SignalRContext'
import type { CrmAction, CrmUser, NamedItem } from '@/models/api'
import { readError } from '@/services/apiClient'
import {
  actionPageSize,
  loadActions,
  loadAssignableUsers,
  loadCustomerCategories,
  reassignAction,
  rescheduleActions,
  saveAction,
} from '@/services/actionService'
import { loadCustomer, searchCustomers } from '@/services/customerService'

function statusLabel(status: string) {
  switch (status) {
    case 'OVERDUE':
      return 'Overdue'
    case 'TODAY':
      return 'Due today'
    case 'TOMORROW':
      return 'Tomorrow'
    case 'CLOSED':
      return 'Completed'
    default:
      return 'Pending'
  }
}

function formatWhen(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function dateInputValue(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 16)
}

function toIso(value: string) {
  const date = value.length === 10 ? new Date(`${value}T09:00`) : new Date(value)
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString()
}


function toggleValue<T>(values: T[], value: T) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

export function LiveActionsScreen({ team = false }: { team?: boolean }) {
  const { session } = useAuth()
  const { lastEvent } = useCrmHub()
  const [params] = useSearchParams()
  const dueParam = params.get('due')
  const [rows, setRows] = useState<CrmAction[]>([])
  const [page, setPage] = useState(1)
  const [users, setUsers] = useState<CrmUser[]>([])
  const [categories, setCategories] = useState<NamedItem[]>([])
  const [filtersReady, setFiltersReady] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [searchDraft, setSearchDraft] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [due, setDue] = useState<'All' | 'Overdue' | 'Today'>(dueParam === 'Overdue' || dueParam === 'Today' ? dueParam : 'All')
  const actionTypes = useActionTypes()
  const storedActionTypes = actionTypes.map(value => ({ value, label: value }))
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  useEffect(() => setSelectedTypes(actionTypes), [actionTypes])
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([])
  const [assigneeId, setAssigneeId] = useState('')
  const [includeClosed, setIncludeClosed] = useState(false)
  const [checkedIds, setCheckedIds] = useState<string[]>([])
  const [selected, setSelected] = useState<CrmAction | null>(null)
  const [bulk, setBulk] = useState<'reschedule' | 'reassign' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setFiltersReady(false)
    Promise.all([loadAssignableUsers(), loadCustomerCategories()])
      .then(([nextUsers, nextCategories]) => {
        if (cancelled) return
        setUsers(nextUsers)
        setCategories(nextCategories)
        setSelectedCategoryIds(nextCategories.map((item) => item.id))
        setFiltersReady(true)
      })
      .catch(() => { if (!cancelled) setFiltersReady(true) })
    return () => { cancelled = true }
  }, [session?.businessId])

  useEffect(() => {
    if (!lastEvent) return
    if (affectsWorkspace(lastEvent, 'actions')) {
      setReloadKey((value) => value + 1)
    }
  }, [lastEvent])

  useEffect(() => {
    setPage(1)
    setCheckedIds([])
  }, [filterText, appliedSearch, team, session?.businessUserId, includeClosed, selectedTypes, selectedCategoryIds, assigneeId, due])

  useEffect(() => {
    if (!filtersReady) return
    let cancelled = false
    setError(null)
    loadActions({
      assignedToId: team ? assigneeId || undefined : session?.businessUserId ?? undefined,
      includeClosed,
      showFor: due,
      search: appliedSearch,
      pageSize: 0,
    })
      .then((next) => {
        if (cancelled) return
        setRows(next.rows)
        setCheckedIds((current) => current.filter((id) => next.rows.some((row) => row.id === id)))
      })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [filtersReady, team, session?.businessUserId, includeClosed, assigneeId, due, appliedSearch, reloadKey])

  const filtered = useMemo(() => {
    const term = filterText.trim().toLowerCase()
    const categoryNames = categories
      .filter((item) => selectedCategoryIds.includes(item.id))
      .map((item) => item.name.toLowerCase())
    const allTypes = selectedTypes.length === storedActionTypes.length
    const allCategories = !categories.length || selectedCategoryIds.length === categories.length
    return rows.filter((row) => {
      if (term && !`${row.customerName ?? ''} ${row.customerPostcode ?? ''} ${row.actionDetail ?? ''} ${row.customerAccountNumber ?? ''}`.toLowerCase().includes(term)) return false
      if (!allTypes && !selectedTypes.some((type) => type.toLowerCase() === row.actionType.toLowerCase())) return false
      if (!allCategories && !categoryNames.includes((row.customerCategory ?? '').toLowerCase())) return false
      return true
    })
  }, [rows, filterText, selectedTypes, selectedCategoryIds, categories])
  const visible = filtered.slice((page - 1) * actionPageSize, page * actionPageSize)
  const pageIds = visible.map((action) => action.id)
  const allPageChecked = pageIds.length > 0 && pageIds.every((id) => checkedIds.includes(id))
  const checkedActions = rows.filter((row) => checkedIds.includes(row.id))

  return (
    <>
      <AdminPageHero
        eyebrow="Workspace / CRM"
        title={team ? 'All actions' : 'My actions'}
        summary={`Calls, visits and follow-ups ${forThisBusiness(session)}.`}
      >
        <Link className="btn btn-primary" to="/actions/new">+ Add action</Link>
      </AdminPageHero>
      <div className="d-flex gap-2 mb-3">
        <Link className={`btn btn-${team ? 'outline-' : ''}primary`} to="/actions/mine">My actions</Link>
        <Link className={`btn btn-${team ? '' : 'outline-'}primary`} to="/actions/team">All actions</Link>
      </div>
      <section className="admin-panel">
          <Row className="g-2 align-items-center">
            <Col lg={4}>
              <Form.Control
                value={filterText}
                placeholder="filter listed actions..."
                aria-label="Filter listed actions"
                onChange={(event) => setFilterText(event.target.value)}
              />
            </Col>
            <Col lg={4}>
              <InputGroup className="actions-search-group">
                <Form.Control
                  value={searchDraft}
                  placeholder="search..."
                  aria-label="Search for actions"
                  onChange={(event) => setSearchDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      setFilterText('')
                      setAppliedSearch(searchDraft.trim())
                    }
                  }}
                />
                <Button variant="outline-success" title="Search for actions" onClick={() => { setFilterText(''); setAppliedSearch(searchDraft.trim()) }}>
                  <FontAwesomeIcon icon={faBinoculars} />
                </Button>
                <Button variant="outline-primary" title="Clear search" onClick={() => { setSearchDraft(''); setAppliedSearch('') }}>
                  <FontAwesomeIcon icon={faRotateRight} />
                </Button>
              </InputGroup>
            </Col>
            <Col lg={4} className="d-flex flex-wrap align-items-center gap-2">
              <CheckMenu menuId="action-types" label="Action Types" variant="secondary" options={storedActionTypes.map((item) => ({ value: item.value, label: item.label }))} selected={selectedTypes} onToggle={(value) => setSelectedTypes((current) => toggleValue(current, value))} />
              <CheckMenu menuId="action-category" label="Category" variant="warning" options={categories.map((item) => ({ value: String(item.id), label: item.name }))} selected={selectedCategoryIds.map(String)} onToggle={(value) => setSelectedCategoryIds((current) => toggleValue(current, Number(value)))} />
              <Form.Check id="actions-show-completed" label="Show completed" checked={includeClosed} onChange={(event) => setIncludeClosed(event.target.checked)} />
            </Col>
          </Row>
          <Row className="g-2 align-items-center mt-2 mb-3">
            {team && (
              <Col lg={6}>
                <Form.Select aria-label="All users" value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
                  <option value="">All users</option>
                  {users.map((user) => <option key={user.crmAccountUserId} value={user.crmAccountUserId}>{user.fullName || user.emailAddress}</option>)}
                </Form.Select>
              </Col>
            )}
            <Col lg={team ? 6 : 12} className="d-flex flex-wrap gap-2 justify-content-lg-end">
              {checkedActions.length === 0 ? (
                <>
                  <Button variant={due === 'All' ? 'primary' : 'outline-primary'} onClick={() => setDue('All')}>Show ALL</Button>
                  <Button variant={due === 'Overdue' ? 'danger' : 'outline-danger'} onClick={() => setDue('Overdue')}>Show OVERDUE</Button>
                  <Button variant={due === 'Today' ? 'success' : 'outline-success'} onClick={() => setDue('Today')}>Show TODAY</Button>
                  <Button variant="outline-info" onClick={() => setReloadKey((value) => value + 1)}><FontAwesomeIcon icon={faRotateRight} /> Refresh</Button>
                </>
              ) : (
                <>
                  <Button variant="primary" onClick={() => setBulk('reschedule')}>Reschedule</Button>
                  <Button variant="info" onClick={() => setBulk('reassign')}>Re-Assign</Button>
                </>
              )}
            </Col>
          </Row>
        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
        {loading ? <LoadingSpinner text="Loading actions" size="5x" /> : <>
        {filtered.length > 0 && (
          <ResultsPager page={page} pageSize={actionPageSize} total={filtered.length} onPage={setPage} />
        )}
        <div className="table-responsive border rounded-3 mb-3">
        <Table hover striped className="mb-0">
          <thead>
            <tr>
              <th>
                <Form.Check
                  aria-label="Select all on current page"
                  title="Select all on current page"
                  checked={allPageChecked}
                  disabled={!pageIds.length}
                  onChange={(event) => {
                    setCheckedIds((current) => event.target.checked
                      ? [...new Set([...current, ...pageIds])]
                      : current.filter((id) => !pageIds.includes(id)))
                  }}
                />
              </th>
              <th>Status</th>
              <th>Action</th>
              <th>Category</th>
              {team && <><th>Assigned to</th><th>Account owner</th></>}
              <th>Acc.#</th>
              <th>Customer</th>
              <th>Postcode</th>
              <th>To Do</th>
              <th>Next Action On</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((action) => (
              <tr key={action.id}>
                <td>
                  <Form.Check
                    aria-label={`Select ${action.customerName ?? 'action'}`}
                    checked={checkedIds.includes(action.id)}
                    onChange={(event) => setCheckedIds((current) => event.target.checked ? [...current, action.id] : current.filter((id) => id !== action.id))}
                  />
                </td>
                <td>
                  <Button size="sm" variant={action.status === 'OVERDUE' ? 'outline-danger' : 'outline-primary'} onClick={() => setSelected(action)}>
                    {statusLabel(action.status)}
                  </Button>
                  {!action.dateClosed && <ActionPresenceIcon actionId={action.id} />}
                </td>
                <td>{action.actionType}</td>
                <td>{action.customerCategory}</td>
                {team && <><td>{action.assignedToName}</td><td>{action.customerAccountOwnerName}</td></>}
                <td><Link to={`/customers/${action.customerId}`}>{action.customerAccountNumber}</Link></td>
                <td><Link to={`/customers/${action.customerId}`}>{action.customerName}</Link></td>
                <td>{action.customerPostcode}</td>
                <td>{action.actionDetail}</td>
                <td>{formatWhen(action.actionDate)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        </div>
        {!filtered.length && (
          <p className="mt-3 mb-0">
            {team
              ? 'No actions match these filters.'
              : <>No open actions are assigned to you. <Link to="/actions/team">All actions</Link> lists every open action {forThisBusiness(session)}.</>}
          </p>
        )}
        {filtered.length > 0 && (
          <ResultsPager page={page} pageSize={actionPageSize} total={filtered.length} onPage={setPage} />
        )}
        </>}
      </section>
      {selected && (
        <LiveActionWorkspace
          action={selected}
          users={users}
          onClose={() => setSelected(null)}
          onChanged={() => setReloadKey((value) => value + 1)}
        />
      )}
      {bulk && (
        <BulkActionModal
          mode={bulk}
          actions={checkedActions}
          users={users}
          onClose={() => setBulk(null)}
          onDone={() => {
            setBulk(null)
            setCheckedIds([])
            setReloadKey((value) => value + 1)
          }}
        />
      )}
    </>
  )
}

function CheckMenu({
  menuId,
  label,
  variant,
  options,
  selected,
  onToggle,
}: {
  menuId: string
  label: string
  variant: string
  options: Array<{ value: string; label: string }>
  selected: string[]
  onToggle: (value: string) => void
}) {
  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle variant={variant}>{label}</Dropdown.Toggle>
      <Dropdown.Menu style={{ maxHeight: 280, overflowY: 'auto' }}>
        {options.map((option, index) => (
          <div className="dropdown-item" key={option.value}>
            <Form.Check
              id={`${menuId}-${index}`}
              label={option.label}
              checked={selected.includes(option.value)}
              onChange={() => onToggle(option.value)}
            />
          </div>
        ))}
        {!options.length && <Dropdown.Item disabled>None available</Dropdown.Item>}
      </Dropdown.Menu>
    </Dropdown>
  )
}

function BulkActionModal({
  mode,
  actions,
  users,
  onClose,
  onDone,
}: {
  mode: 'reschedule' | 'reassign'
  actions: CrmAction[]
  users: CrmUser[]
  onClose: () => void
  onDone: () => void
}) {
  const [due, setDue] = useState(dateInputValue(new Date(Date.now() + 86400000).toISOString()))
  const [assignee, setAssignee] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (mode === 'reschedule') {
        await rescheduleActions(actions.map((action) => action.id), toIso(due))
      } else {
        await Promise.all(actions.map((action) => reassignAction(action.id, assignee)))
      }
      onDone()
    } catch (caught) {
      setError(readError(caught))
      setBusy(false)
    }
  }

  return (
    <Modal show onHide={onClose}>
      <Form onSubmit={(event) => void submit(event)}>
        <Modal.Header closeButton>
          <Modal.Title>{mode === 'reschedule' ? 'Reschedule action(s)' : 'Re-Assign action(s)'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <p>{actions.length} selected.</p>
          {mode === 'reschedule' ? (
            <Field label="Select new date" type="datetime-local" value={due} onChange={setDue} required />
          ) : (
            <UserSelect label="Assigned user" users={users} value={assignee} onChange={setAssignee} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy}>{mode === 'reschedule' ? 'Reschedule' : 'Re-Assign'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

function UserSelect({ label, users, value, onChange }: { label: string; users: CrmUser[]; value: string; onChange: (value: string) => void }) {
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Select value={value} onChange={(event) => onChange(event.target.value)} required>
        <option value="">Select a user</option>
        {users.map((user) => <option key={user.crmAccountUserId} value={user.crmAccountUserId}>{user.fullName || user.emailAddress}</option>)}
      </Form.Select>
    </Form.Group>
  )
}

export function LiveAddActionScreen() {
  const actionTypes = useActionTypes()
  const { customerId } = useParams()
  const { session } = useAuth()
  const { notify } = useCrmHub()
  const navigate = useNavigate()
  const [users, setUsers] = useState<CrmUser[]>([])
  const [customerQuery, setCustomerQuery] = useState('')
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([])
  const [selectedCustomer, setSelectedCustomer] = useState(customerId ?? '')
  const [customerName, setCustomerName] = useState('')
  const [type, setType] = useState<string>('')
  useEffect(() => { if (!type && actionTypes.length) setType(actionTypes[0]) }, [type, actionTypes])
  const [assignee, setAssignee] = useState(session?.businessUserId ?? '')
  const [due, setDue] = useState(todayDateKey)
  const [detail, setDetail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    loadAssignableUsers().then(setUsers).catch((caught) => setError(readError(caught)))
  }, [])

  useEffect(() => {
    if (!customerId) return
    let cancelled = false
    loadCustomer(customerId)
      .then((row) => { if (!cancelled) setCustomerName(row.name) })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [customerId])

  async function findCustomers(event: FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      const rows = await searchCustomers({ name: customerQuery })
      setCustomers(rows.map((row) => ({ id: row.id, name: row.name })))
      if (!rows.some((row) => row.id === selectedCustomer)) setSelectedCustomer(rows[0]?.id ?? '')
    } catch (caught) {
      setError(readError(caught))
    }
  }

  return (
    <>
      <AdminPageHero eyebrow="Workspace / CRM" title="Add customer action" summary="Choose a customer and plan the next conversation." />
      <section className="admin-panel form-panel">
          {error && <Alert variant="danger">{error}</Alert>}
          {!customerId && (
            <Form onSubmit={findCustomers} className="mb-3">
              <Row className="g-2 align-items-end">
                <Col md={8}><Field label="Find customer" value={customerQuery} onChange={setCustomerQuery} /></Col>
                <Col md={4}><Button type="submit" className="mb-3">Find</Button></Col>
              </Row>
            </Form>
          )}
          <Form onSubmit={(event) => {
            event.preventDefault()
            setBusy(true)
            setError(null)
            void saveAction({
              customerId: selectedCustomer,
              assignedToId: assignee,
              actionType: type,
              actionDetail: detail,
              actionDate: dueDateIso(due),
            })
              .then(async (id) => {
                await notify('SendActionAdded', { actionId: id, organisationId: selectedCustomer, userId: session?.userId, text: detail })
                navigate(teamPath(assignee, session?.businessUserId))
              })
              .catch((caught) => setError(readError(caught)))
              .finally(() => setBusy(false))
          }}>
            {customerId ? (
              <p><strong>Customer</strong><br />{customerName || 'Selected customer'}</p>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Customer</Form.Label>
                <Form.Select required value={selectedCustomer} onChange={(event) => setSelectedCustomer(event.target.value)}>
                  <option value="">Select customer</option>
                  {customers.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                </Form.Select>
              </Form.Group>
            )}
            <DueDateField label="Due" value={due} onChange={setDue} required />
            <Field label="Action type" options={[...actionTypes]} value={type} onChange={setType} />
            <UserSelect label="Assign to" users={users} value={assignee} onChange={setAssignee} />
            <Field label="Action text" type="textarea" value={detail} onChange={setDetail} required />
            <Button type="submit" disabled={busy}>Save action</Button>{' '}
            <Link className="btn btn-outline-secondary" to="/actions/mine">Cancel</Link>
          </Form>
      </section>
    </>
  )
}

function teamPath(assignee: string, userId: string | null | undefined) {
  return assignee && assignee !== userId ? '/actions/team' : '/actions/mine'
}
